import { firestore, functions } from 'core/config/firebase';
import {
  addDoc,
  CollectionReference,
  deleteDoc,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  getDoc,
  getDocs,
  onSnapshot,
  Query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { StorageReference, uploadString } from 'firebase/storage';
import { IGetData, IListAllData } from './api-interface';

const listAllData = async <T>(query: Query<DocumentData>): Promise<IListAllData<T>> => {
  const converter = typeConverter<T>();
  const querySnapshot = await getDocs(query.withConverter(converter));
  const result: IListAllData<T> = {
    data: [],
    count: querySnapshot.size,
  };
  querySnapshot.forEach((doc) => {
    result.data.push(doc.data());
  });
  return result;
};

const getData = async <T>(docRef: DocumentReference): Promise<IGetData<T>> => {
  const converter = typeConverter<T>();
  const docSnapshot = await getDoc(docRef.withConverter(converter));
  if (docSnapshot.exists()) {
    return {
      data: docSnapshot.data(),
    };
  } else {
    throw new Error('This document does not exist');
  }
};

const getSnapshot = async <T>(docRef: DocumentReference): Promise<DocumentSnapshot<T>> => {
  const converter = typeConverter<T>();
  return getDoc(docRef.withConverter(converter));
};

const listAll = async <T>(query: Query<DocumentData>): Promise<QuerySnapshot<T>> => {
  const converter = typeConverter<T>();
  return getDocs(query.withConverter(converter));
};

const add = async <T>(collectionRef: CollectionReference, docContent: T) => {
  return addDoc(collectionRef, docContent);
};

const set = async <T>(docRef: DocumentReference, docContent: T) => {
  return setDoc(docRef, docContent);
};

const update = async <T>(docRef: DocumentReference, docContent: T) => {
  return updateDoc(docRef, docContent);
};

const remove = async (docRef: DocumentReference) => {
  return deleteDoc(docRef);
};

const callFunction = async <T>(payload: any, name: string) => {
  const fbFunction = httpsCallable(functions, name);
  const result = await fbFunction(payload);
  return result.data as T;
};

const subscribeToDoc = <T>(docRef: DocumentReference, resultHandler: Function, errorHandler: Function) => {
  const converter = typeConverter<T>();
  return onSnapshot(
    docRef.withConverter(converter),
    (doc) => {
      resultHandler(doc.data());
    },
    (error) => {
      errorHandler(error);
    }
  );
};

const subscribeToCollection = (
  collectionRef: CollectionReference | Query,
  resultHandler: Function,
  errorHandler: Function
) => {
  return onSnapshot(
    collectionRef,
    (snapShot) => {
      resultHandler(snapShot);
    },
    (error) => {
      errorHandler(error);
    }
  );
};

const typeConverter = <T>() => ({
  toFirestore: (data: T) => data,
  fromFirestore: (doc: QueryDocumentSnapshot<DocumentData>) => doc.data() as T,
});

const batchRemove = (docRefs: DocumentReference[]) => {
  const batch = writeBatch(firestore);
  docRefs.forEach((ref) => {
    batch.delete(ref);
  });
  return batch.commit();
};

const uploadDataUrl = (storageRef: StorageReference, dataUrl: string, contentType: string) => {
  const metadata = {
    contentType,
  };
  return uploadString(storageRef, dataUrl, 'data_url', metadata);
};

const ApiService = {
  getSnapshot,
  listAllData,
  getData,
  callFunction,
  add,
  set,
  update,
  remove,
  subscribeToDoc,
  subscribeToCollection,
  batchRemove,
  uploadDataUrl,
  listAll,
};

export default ApiService;
